import { conversationExplorerService } from '../conversationExplorer/conversationExplorerService';
import { getUserBehaviorAnalytics } from '../user-behavior/userBehaviorService';
import { resolveAgentName } from '../../utils/agentName';

const firstValue = (record, ...keys) => {
  const attributes = record?.attributes || {};
  for (const key of keys) {
    if (record?.[key] !== undefined && record?.[key] !== null) return record[key];
    if (attributes[key] !== undefined && attributes[key] !== null) return attributes[key];
  }
  return undefined;
};

const asNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const asList = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (typeof value === 'string') return value.split(',').map((item) => item.trim()).filter(Boolean);
  return [];
};

const normalizeSeverity = (value) => String(value || 'none').trim().toLowerCase();

const contentStatusFor = (record, prompt, response) => {
  const reported = firstValue(record, 'content_status', 'contentStatus');
  if (reported) return reported;
  const dataMode = firstValue(record, 'saf3ai.data_mode', 'data_policy');
  if (dataMode === 'stripped' || dataMode === 'metadata_only') return 'policy_stripped';
  return prompt || response ? 'available' : 'source_missing';
};

export const normalizeCustomSession = (trace = {}) => {
  const sessionId = trace.conversation_id || trace.session_id || trace.trace_id || trace.id || trace._id;
  const success = trace.success_rate ?? trace.completion_rate;
  return {
    ...trace,
    session_id: sessionId,
    conversation_id: sessionId,
    agent_name: resolveAgentName(trace),
    user_email: trace.user_email || trace.user_id || trace.raw_user_id || '',
    interactions: asNumber(trace.interactions ?? trace.span_count ?? trace.total_spans),
    threats_in_session: asNumber(trace.threats_in_session ?? trace.threat_count ?? trace.total_threats),
    success_rate: success == null ? null : asNumber(success),
    avg_grounding: trace.avg_grounding ?? trace.grounding_coverage ?? null,
    start_time: trace.start_time || trace.start_time_iso || trace.first_seen,
    end_time: trace.end_time || trace.end_time_iso || trace.last_seen,
    last_activity: trace.last_activity || trace.last_seen || trace.end_time || trace.start_time_iso,
    methods: asList(trace.methods || trace.frameworks || trace.framework),
    risk_score: trace.risk_score ?? trace.threat_score ?? null,
    max_severity: normalizeSeverity(trace.max_severity || trace.severity),
    content_status: trace.content_status || 'source_missing',
    trace_ids: asList(trace.trace_ids || trace.trace_id),
  };
};

export const normalizeCustomInteraction = (span = {}, index = 0) => {
  const threatAnalysis = span['enrichment.threat_analysis'] || span.enrichment?.threat_analysis || {};
  const prompt = firstValue(
    span,
    'prompt', 'prompt_user_only', 'enrichment.input_text', 'llm.prompt',
    'gen_ai.prompt', 'gen_ai.user_input', 'security.scan.prompt',
  );
  const response = firstValue(
    span,
    'response', 'output_text', 'enrichment.output_text', 'llm.response',
    'gen_ai.response', 'security.scan.response',
  );
  const statusCode = span.status?.code ?? span.status;
  const severity = normalizeSeverity(
    span.threat_severity || span.max_severity || threatAnalysis.severity,
  );
  const threatTypes = asList(span.threat_types || threatAnalysis.threat_types);

  return {
    ...span,
    interaction_id: span.interaction_id || span._id || span.spanId || `span-${index}`,
    timestamp: span.timestamp || span.startTime || span['@timestamp'],
    prompt,
    response,
    agent_name: resolveAgentName(span),
    user_email: firstValue(span, 'user_email', 'user.id', 'enduser.id'),
    content_status: contentStatusFor(span, prompt, response),
    threat_severity: severity,
    threats: threatTypes,
    threat_categories: threatTypes,
    scan_status: firstValue(span, 'scan_status', 'security.scan.status'),
    state: statusCode === 2 || String(statusCode).toLowerCase() === 'error' ? 'FAILED' : 'COMPLETED',
    trace_id: span.traceId || span.trace_id,
    span_id: span.spanId || span.span_id,
  };
};

const toConversationParams = (params = {}) => {
  const next = {
    page: params.page || 1,
    per_page: params.page_size || params.per_page || 20,
    time_range: params.time_range || 'last_30_days',
  };
  if (params.start_time) next.start_date = params.start_time;
  if (params.end_time) next.end_date = params.end_time;
  if (params.agent_name) next.agent_identifier = String(params.agent_name).split(',')[0];
  if (params.user_email) next.user_id = String(params.user_email).split(',')[0];
  if (params.has_threats === true) next.status = 'threat';
  if (params.has_threats === false) next.status = 'clean';
  return next;
};

export const customAgentsConversationAdapter = Object.freeze({
  async getSessions(params = {}) {
    const data = await conversationExplorerService.getTraces(toConversationParams(params));
    return {
      sessions: (data.traces || []).map(normalizeCustomSession),
      total: data.total || 0,
      page: data.page || params.page || 1,
      page_size: data.per_page || params.page_size || 20,
      total_pages: data.total_pages || 1,
    };
  },

  async getSessionDetails(sessionId, params = {}) {
    const data = await conversationExplorerService.getConversationDetails(
      sessionId,
      toConversationParams(params),
    );
    const spans = data?.spans || data?.interactions || data?.timeline || [];
    return {
      ...data,
      interactions: spans.map(normalizeCustomInteraction),
      total: data?.total_spans ?? spans.length,
    };
  },

  async getThreats(params = {}) {
    const data = await conversationExplorerService.getTraces({
      ...toConversationParams(params),
      status: 'threat',
      per_page: Math.min(params.page_size || 200, 200),
    });
    return {
      threats: (data.traces || []).map((trace) => ({
        session_id: trace.conversation_id || trace.trace_id,
        severity: normalizeSeverity(trace.max_severity || trace.severity),
      })),
      // The explorer cannot filter by severity, so a severity-scoped total is
      // unknown (null) rather than the count of every threat (audit row D7).
      total: params.severity && params.severity !== 'all' ? null : (data.total ?? null),
    };
  },

  getConversationDriftAnalysis: (params) => (
    conversationExplorerService.getConversationDriftAnalysis(params)
  ),
});

const riskLevelFor = (score, threshold = 50) => {
  const value = asNumber(score);
  if (value >= threshold) return 'high';
  if (value >= threshold * 0.6) return 'medium';
  if (value > 0) return 'low';
  return 'none';
};

export const normalizeCustomUser = (user = {}, threshold = 50) => {
  const userId = user.raw_user_id || user.user_id || user.user_email || user.email || user.user_name;
  const threatTypes = asList(user.threat_types);
  const threatCount = asNumber(user.threats ?? user.threat_count ?? user.total_threats, threatTypes.length);
  const agents = Array.isArray(user.agents_used)
    ? user.agents_used
    : asList(user.applications_used || user.agents || user.agent_names);
  return {
    ...user,
    user_email: userId || 'anonymous',
    total_interactions: asNumber(user.total_interactions ?? user.total_prompts),
    sessions: asNumber(user.sessions ?? user.conversation_count ?? user.total_conversations),
    applications_used: agents,
    agents_used: Array.isArray(user.agents_used) ? user.agents_used.length : asNumber(user.agents_used, agents.length),
    threats: threatCount,
    critical_threats: asNumber(user.critical_threats),
    high_threats: asNumber(user.high_threats),
    medium_threats: asNumber(user.medium_threats),
    low_threats: asNumber(user.low_threats),
    risk_score: asNumber(user.risk_score),
    risk_level: user.risk_level || riskLevelFor(user.risk_score, threshold),
    last_activity: user.last_activity || user.last_assessed,
  };
};

export const customAgentsUsersAdapter = Object.freeze({
  async getUsers(params = {}) {
    const request = {};
    if (params.start_time) request.start_date = params.start_time;
    if (params.end_time) request.end_date = params.end_time;
    if (params.time_range) request.time_range = params.time_range;
    if (params.agent_name) request.agent_id = String(params.agent_name).split(',')[0];

    const response = await getUserBehaviorAnalytics(request);
    const data = response?.data || response || {};
    const threshold = asNumber(data.kpis?.high_risk_threshold, 50);
    let users = (data.watchlist || data.users || []).map((user) => normalizeCustomUser(user, threshold));
    if (params.user_email) {
      const wanted = new Set(String(params.user_email).split(',').map((value) => value.trim()));
      users = users.filter((user) => wanted.has(user.user_email));
    }
    return {
      users,
      total: data.kpis?.total_users ?? users.length,
      unique_users: data.kpis?.total_users ?? users.length,
      by_risk: {
        high: data.kpis?.high_risk_users ?? users.filter((user) => user.risk_level === 'high').length,
        medium: users.filter((user) => user.risk_level === 'medium').length,
        low: users.filter((user) => user.risk_level === 'low').length,
        none: users.filter((user) => user.risk_level === 'none').length,
      },
    };
  },
});
